import React, { useEffect, useState } from 'react';
import BankSearchModal from './BankSearchModal';
import GroupSearchModal from './GroupSearchModal';
import ScopeSearchModal from './ScopeSearchModal';
import { listGroups, resolveBankByIdentifier, searchSupervisors } from '../api/banks';

/**
 * Champ de PORTEE unique du portail company (2026-09-26) — Groupe, Superviseur ou
 * Banque. Remplace les champs texte bruts de ~20 pages, qui envoyaient tel quel
 * ce qu'on tapait comme identifiant INTERNE (alors qu'on tape le numero visible
 * `seq` / un username / un nom) ou dont la 🔍 ne faisait rien.
 *   - on tape : le NUMERO (seq) partout, le USERNAME (banque, superviseur) ou le
 *     NOM (groupe) ; resolution au blur (ou Entree) -> "Introuvable" sinon ;
 *   - 🔍 -> la bonne liste de recherche (banques / groupes / superviseurs) ;
 *   - le nom resolu est affiche PRECEDE de son numero ("3 - jean jeudy") ; un
 *     groupe ou un superviseur s'affiche "<nom>-<N°>" ("group1-1", display_name
 *     cote backend).
 * La page garde la source de verite : `id` (id interne, pour les appels API) et
 * `name` (libelle affiche) ; recoit `onResolve(id, label)` ('' , '' = vide).
 */

export const scopeKind = (filter) => {
  const f = String(filter || '').toLowerCase();
  if (f.startsWith('group')) return 'group';
  if (f.startsWith('super')) return 'supervisor';
  if (f.startsWith('bank') || f.startsWith('banque')) return 'bank';
  return null;
};

export const labelWithSeq = (seq, name) => (seq != null && seq !== '' ? `${seq} - ${name || ''}` : (name || ''));

const NOT_FOUND = { bank: 'Banque introuvable', group: 'Groupe introuvable', supervisor: 'Superviseur introuvable' };

async function resolveScope(kind, text) {
  const t = text.trim();
  const low = t.toLowerCase();
  if (kind === 'bank') {
    const bank = await resolveBankByIdentifier(t);
    return bank ? { id: bank.id, label: labelWithSeq(bank.seq, bank.name) } : null;
  }
  if (kind === 'group') {
    const groups = await listGroups().catch(() => []);
    const g = groups.find((x) => String(x.seq) === t)
      || groups.find((x) => (x.name || '').toLowerCase() === low)
      || groups.find((x) => (x.display_name || '').toLowerCase() === low);
    // Groupe : display_name contient deja le N° ("group1-1").
    return g ? { id: g.id, label: g.display_name || g.name } : null;
  }
  if (kind === 'supervisor') {
    const sups = await searchSupervisors().catch(() => []);
    const s = sups.find((x) => String(x.seq) === t) || sups.find((x) => (x.username || '').toLowerCase() === low);
    // Superviseur : display_name contient deja le N° ("jean-3").
    return s ? { id: s.id, label: s.display_name || labelWithSeq(s.seq, s.name) } : null;
  }
  return null;
}

export default function ScopeField({
  filter,
  id,
  name = '',
  onResolve,
  inputClass = '',
  iconClass = '',
  nameClass = '',
  errClass = '',
}) {
  const kind = scopeKind(filter);
  const [text, setText] = useState('');
  const [err, setErr] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  // Portee changee ou valeur effacee par la page -> case videe.
  useEffect(() => { setText(''); setErr(''); }, [kind]);
  useEffect(() => { if (!id || id === '0') setText(''); }, [id]);

  if (!kind) return null;

  const resolve = async () => {
    if (text.trim() === '') { setErr(''); onResolve('', ''); return; }
    const res = await resolveScope(kind, text);
    if (res) { setErr(''); onResolve(String(res.id), res.label); } else { setErr(NOT_FOUND[kind]); }
  };

  const pick = (pid, label, seq) => {
    setText(seq != null ? String(seq) : '');
    setErr('');
    onResolve(String(pid), label);
  };

  return (
    <>
      <input
        className={inputClass}
        type="text"
        value={text}
        placeholder={kind === 'group' ? 'N° ou nom' : 'N° ou username'}
        onChange={(e) => { setText(e.target.value); setErr(''); }}
        onFocus={(e) => e.target.select()}
        onBlur={resolve}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); resolve(); } }}
      />
      <span className={iconClass} onClick={() => setModalOpen(true)} style={{ cursor: 'pointer' }}>🔍</span>
      {err
        ? <span className={errClass || nameClass} style={{ color: '#c0392b' }}>{err}</span>
        : (name ? <span className={nameClass}>{name}</span> : null)}

      {modalOpen && kind === 'bank' && (
        <BankSearchModal
          onSelect={(bid, bname, busername, bseq) => pick(bid, labelWithSeq(bseq, bname), bseq ?? busername)}
          onClose={() => setModalOpen(false)}
        />
      )}
      {modalOpen && kind === 'group' && (
        <GroupSearchModal
          onSelect={(gid, gname, gseq) => pick(gid, gname, gseq)}
          onClose={() => setModalOpen(false)}
        />
      )}
      {modalOpen && kind === 'supervisor' && (
        <ScopeSearchModal
          filter="Supervisor"
          onSelect={(sid, sname, sseq) => pick(sid, sname, sseq)}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}
